import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, User, LogOut, Settings, CreditCard } from "lucide-react";
import logo from "../../assets/Easymarg logo.png";
import pf from "../../assets/Group 29.png";
import axiosInstance from "../../api/axiosInstance";
import NotificationBell from "../notifications/NotificationBell";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const initialUserId = localStorage.getItem("userId");
  const initialUserName = localStorage.getItem("userName");

  const [userId, setUserId] = useState(initialUserId);
  const [localName, setLocalName] = useState(initialUserName);
  const [userProfile, setUserProfile] = useState(null);

  // Helper to resolve image URL
  const getImageUrl = (pic) => {
    if (!pic) return pf;
    if (pic.startsWith("http")) return pic;
    const baseUrl = process.env.REACT_APP_API_URL || "https://backend.webbirth.online:5030";
    const imageBaseUrl = baseUrl.replace('/api', '');
    const cleanPic = pic.startsWith('/') ? pic.substring(1) : pic;
    return `${imageBaseUrl}/${cleanPic}`;
  };

  const storedPic = userId ? localStorage.getItem(`profilePic_${userId}`) : null;
  const [imagePreview, setImagePreview] = useState(getImageUrl(storedPic));
  const location = useLocation();

  // 🔥 UPDATED: Clean Sign Out Logic with Server Notification
  const handleSignOut = async () => {
    setIsProfileOpen(false);
    setIsOpen(false);
    
    try {
      await axiosInstance.post("/users/logout");
    } catch (_) {}

    // 1. Clear all local and session storage to remove tokens/IDs
    localStorage.clear();
    sessionStorage.clear();
    
    // 2. Notify other components of the auth change
    window.dispatchEvent(new Event("authChange"));
    
    // 3. Force redirect and REPLACE the history stack entry.
    // This prevents the "Back" button from showing the previous protected page.
    window.location.replace("/");
  };

  useEffect(() => {
    const syncAuth = () => {
      const id = localStorage.getItem("userId");
      const name = localStorage.getItem("userName");
      const savedPic = id ? localStorage.getItem(`profilePic_${id}`) : null;
      setUserId(id);
      setLocalName(name);
      setImagePreview(getImageUrl(savedPic));
      if (!id) {
        setImagePreview(pf);
        setUserProfile(null);
      }
    };
    window.addEventListener("authChange", syncAuth);
    return () => window.removeEventListener("authChange", syncAuth);
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) return;
      try {
        const res = await axiosInstance.get("/users/profile");
        const user = res?.data?.user;
        if (user) {
          setUserProfile(user);
          if (user.name) {
            localStorage.setItem("userName", user.name);
            setLocalName(user.name);
          }
          const profilePic = user.profilePic || user.profilePicture || user.avatar || user.image || user.photo;
          if (profilePic) {
            localStorage.setItem(`profilePic_${userId}`, profilePic);
            setImagePreview(getImageUrl(profilePic));
          }
        }
      } catch (err) {
        console.error("Navbar - Profile fetch error:", err);
      }
    };
    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getLinkClass = (path) =>
    `transition duration-300 font-medium text-[13px] pb-1 border-b-2 ${
      location.pathname.startsWith(path) ? "text-white border-white" : "text-white/70 border-transparent hover:text-white hover:border-white/50"
    }`;

  const getMobileLinkClass = (path) =>
    `block px-6 py-2.5 transition-colors text-sm ${
      location.pathname === path ? "bg-white/10 text-white font-semibold" : "text-white/70 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <nav
      className={`fixed top-0 w-full z-[999] transition-all duration-300 ${
        isScrolled ? "bg-[#08132b]/95 backdrop-blur-lg border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          {/* Left side: Logo */}
          <Link to="/home" className="flex items-center space-x-2 group shrink-0 relative z-10">
            <img src={logo} alt="EasyMarg Logo" className="h-7 object-contain" />
          </Link>

          {/* Right side: Links + Icons */}
          <div className="flex items-center gap-4 lg:gap-6">
            <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
              <Link to="/home" className={getLinkClass("/home")}>Home</Link>
              <Link to="/services" className={getLinkClass("/services")}>Our Services</Link>
              <Link to="/booking" className={getLinkClass("/booking")}>My Bookings</Link>
              <Link to="/contact" className={getLinkClass("/contact")}>Contact Us</Link>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <NotificationBell />

            <div className="hidden md:block relative">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className={`flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full border transition-all duration-300 ${
                  isProfileOpen ? "bg-white border-white shadow-xl" : "bg-transparent border border-white/50 hover:bg-white/10 hover:border-white"
                }`}
              >
                <div className={`h-7 w-7 rounded-full overflow-hidden shadow-sm shrink-0 flex items-center justify-center ${isProfileOpen ? "bg-[#134DA7]/10" : "bg-white/20"}`}>
                  <img
                    src={imagePreview}
                    alt="User"
                    className="w-full h-full object-cover"
                    onError={(e) => { 
                      e.target.style.display = 'none'; 
                      e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="' + (isProfileOpen ? 'text-[#134DA7]' : 'text-white') + '"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
                    }}
                  />
                </div>
                <span className={`text-[13px] font-bold tracking-tight ${isProfileOpen ? "text-[#134DA7]" : "text-white"}`}>
                  {(userProfile?.name || localName || "Account").split(" ")[0]}
                </span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isProfileOpen ? "rotate-180 text-[#134DA7]" : "text-white/60"}`} />
              </motion.button>

              <AnimatePresence>
                {isProfileOpen && (
                  <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[10]" onClick={() => setIsProfileOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-3 w-56 bg-white rounded-[1.5rem] shadow-2xl border border-slate-100 overflow-hidden z-[11]"
                    >
                      <div className="p-4 bg-slate-50/50 border-b border-slate-100">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Signed in as</p>
                        <p className="font-bold text-slate-800 truncate">{userProfile?.name || localName || "User Account"}</p>
                      </div>
                      
                      <div className="p-2">
                        <Link to="/profile-section" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-blue-50 text-slate-600 font-bold text-xs"><User size={16} /> My Profile</Link>
                        <Link to="/profile/my-bookings" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-blue-50 text-slate-600 font-bold text-xs"><CreditCard size={16} /> My Bookings</Link>
                        <Link to="/help" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-blue-50 text-slate-600 font-bold text-xs"><Settings size={16} /> Support</Link>
                        <div className="h-px bg-slate-100 my-1 mx-2" />
                        <button onClick={handleSignOut} className="w-full flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-rose-50 text-slate-600 hover:text-rose-600 transition-all font-bold text-xs text-left">
                          <LogOut size={16} /> Sign Out
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <button
              className="md:hidden h-9 w-9 flex items-center justify-center rounded-full border border-white/50 text-white hover:bg-white/10 hover:border-white transition-all"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden absolute top-20 left-0 w-full bg-[#08132b]/95 backdrop-blur-xl border-b border-white/10 py-3 shadow-2xl">
          <Link onClick={() => setIsOpen(false)} to="/home" className={getMobileLinkClass("/home")}>Home</Link>
          <Link onClick={() => setIsOpen(false)} to="/services" className={getMobileLinkClass("/services")}>Our Services</Link>
          <Link onClick={() => setIsOpen(false)} to="/booking" className={getMobileLinkClass("/booking")}>My Bookings</Link>
          <Link onClick={() => setIsOpen(false)} to="/contact" className={getMobileLinkClass("/contact")}>Contact Us</Link>
          <Link onClick={() => setIsOpen(false)} to="/profile-section" className={getMobileLinkClass("/profile-section")}>Profile</Link>
          <div className="px-6 pt-4 border-t border-white/10 mt-2">
            <button onClick={handleSignOut} className="w-full py-2.5 bg-white/10 hover:bg-rose-500 rounded-xl text-white font-bold transition-all text-sm">Sign Out</button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;